import React, { useCallback, useState } from 'react';
import { Box } from '@mui/material';
import { ListPageFiltersWrapper } from '@components';
import { GridSearch } from 'components/common/grid';
import { InputFieldV2 } from 'components/common/form-controls';
import { useRouter } from 'next/router';
import { debounce, omit } from 'lodash-es';

import { HANDOVER_CARS_LOCATIONS, HANDOVER_CARS_STAGE } from '@constants';
import { useAssignees, useDateTimeRange } from '@hooks';
import { URLS } from 'layouts/DrawerLayout/constants';
import { generateSearchPlaceholder } from '@utils';

export const DRIVERS_SEARCH_PLACEHOLDER =
  'Search by last name, plate, or email'; // Same placeholder for deliveries, returns and inspections

const locations = Object.keys(HANDOVER_CARS_LOCATIONS).reduce(
  (acc, cur) => {
    acc.push({
      text: HANDOVER_CARS_LOCATIONS[cur],
      value: HANDOVER_CARS_LOCATIONS[cur],
    });
    return acc;
  },
  [{ text: '-', value: null }]
);

const types = Object.keys(HANDOVER_CARS_STAGE).reduce(
  (acc, cur) => {
    acc.push({
      text: HANDOVER_CARS_STAGE[cur],
      value: cur,
    });
    return acc;
  },
  [{ text: '-', value: null }]
);
const DriverFilters = ({ type = 'delivery' }) => {
  const router = useRouter();

  const { assigneesWithNone } = useAssignees();

  const { handleDateRangeChange } = useDateTimeRange();

  const [query, setQuery] = useState(router.query?.query || '');

  const handleChange = (value, field) => {
    if (value !== null) {
      router.push({
        pathname:
          type === 'delivery'
            ? URLS.handoverCarsDelivery
            : URLS.handoverCarsReturns,
        query: { page: 1, ...router.query, [field]: value },
      });
    } else {
      router.push({
        pathname:
          type === 'delivery'
            ? URLS.handoverCarsDelivery
            : URLS.handoverCarsReturns,
        query: omit(router.query, field),
      });
    }
  };

  const handleChangeMultiFilter = (val, filterName) => {
    const newQuery = { ...router.query };
    newQuery.page = 1;

    if (val.length) {
      newQuery[filterName] = val.map((item) => item.value);
    } else {
      delete newQuery[filterName];
    }

    router.push({
      pathname:
        type === 'delivery'
          ? URLS.handoverCarsDelivery
          : URLS.handoverCarsReturns,
      query: newQuery,
    });
  };

  const getNormalizedFilter = (filterName, options) => {
    const queryValue = router.query?.[filterName];
    if (!queryValue) return [];

    const values = Array.isArray(queryValue) ? queryValue : [queryValue];

    return values
      .map((value) =>
        options.find((option) => String(option.value) === String(value))
      )
      .filter(Boolean);
  };

  const handleDebounceChangeSearch = (newValue) => {
    if (!newValue) {
      delete router.query.query;
      router.push({
        pathname:
          type === 'delivery'
            ? URLS.handoverCarsDelivery
            : URLS.handoverCarsReturns,
        query: { page: '1' },
      });
    } else {
      router.push({
        pathname:
          type === 'delivery'
            ? URLS.handoverCarsDelivery
            : URLS.handoverCarsReturns,
        query: { page: '1', query: newValue },
      });
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debounceFn = useCallback(debounce(handleDebounceChangeSearch, 100), [
    router.query,
  ]);

  const _handleChangeSearch = (newValue) => {
    setQuery(newValue);
    debounceFn(newValue);
  };

  const handleDateRange = (newValue) => {
    const pathname =
      type === 'delivery'
        ? URLS.handoverCarsDelivery
        : URLS.handoverCarsReturns;
    handleDateRangeChange(newValue, pathname);
  };

  const selectedAssignee = getNormalizedFilter('assignee', assigneesWithNone);

  return (
    <ListPageFiltersWrapper styles={{ top: '80px' }}>
      <Box>
        <GridSearch
          value={query}
          onChange={_handleChangeSearch}
          placeholder={generateSearchPlaceholder(
            [],
            DRIVERS_SEARCH_PLACEHOLDER
          )}
        />
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            width: '100%',
            justifyContent: 'space-between',
            marginTop: '12px',
          }}
        >
          <InputFieldV2
            type="filterDropdown"
            label="Location"
            width="200px"
            value={router.query?.location || ''}
            options={locations}
            onChange={(value) => handleChange(value, 'location')}
          />
          <InputFieldV2
            type="filterDropdown"
            label="Type"
            width="200px"
            value={router.query?.type || ''}
            options={types}
            onChange={(value) => handleChange(value, 'type')}
          />
          <InputFieldV2
            type="filterMultiselect"
            label="Assignees"
            width="200px"
            minWidth="300px"
            limitTags={1}
            hasSelectAllOption
            value={selectedAssignee}
            options={assigneesWithNone}
            onChange={(selectedValue) => {
              handleChangeMultiFilter(selectedValue, 'assignee');
            }}
          />
          <InputFieldV2
            type="filterDateTimeRangePicker"
            label="Date Range"
            width="200px"
            value={[
              router.query?.dateFrom || null,
              router.query?.dateTo || null,
            ]}
            onChange={handleDateRange}
          />
        </Box>
      </Box>
    </ListPageFiltersWrapper>
  );
};

const memoizedDriverFilters = React.memo(DriverFilters);
export { memoizedDriverFilters as DriverFilters };

export default memoizedDriverFilters;
